import { useState } from "react";
import { HomeframeSelect, Link } from "@builtbyted/homeframe";
import {
  Bars,
  Heatmap,
  LineChart,
  sumSeries,
  WorldMap,
  type Location,
  type Series,
} from "./Charts";
import {
  count,
  duration,
  num,
  str,
  timeAgo,
  Page,
  Message,
  Poster,
  Section,
  DayPicker,
  TopList,
  scoped,
  useData,
  useLink,
  useNavigateQuery,
  useQuery,
  type Row,
} from "./ui";

interface Trends {
  available: boolean;
  message: string | null;
  generated_at: number;
  step: number;
  hours: number;
  streams: Series[];
  bandwidth: Series[];
  quality: Series[];
  viewers: Series[];
  hosts: Series[];
  locations: Location[];
  leaderboard: Row[];
  unlocated: number;
}
interface InsightsData {
  plays: number;
  duration: number;
  users: number;
  daily: Row[];
  heatmap: Row[];
  platforms: Row[];
  decisions: Row[];
  resolutions: Row[];
  servers: Row[];
  users_top: Row[];
  media: Row[];
  timezone: string;
  grain: string;
}
interface Server {
  id: number;
  name: string;
  kind: string;
  status: string;
  stream_count: number;
  last_success: number;
}
function Stat({
  label,
  value,
  detail,
  tone = "violet",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: string;
}) {
  return (
    <article className={`stat-tile ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}
function Panel({
  title,
  note,
  children,
  className = "",
}: {
  title: string;
  note: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`viz-panel ${className}`}>
      <div className="viz-heading">
        <h2>{title}</h2>
        <p>{note}</p>
      </div>
      {children}
    </section>
  );
}
export function Dashboard() {
  const url = useQuery(),
    navigate = useNavigateQuery();
  const hours = url.searchParams.get("hours") || "6";
  const trends = useData<Trends>(scoped("/dashboard", url, { hours }), 30000);
  const sessions = useData<Row[]>(scoped("/activity", url), 5000);
  const servers = useData<Server[]>("/servers", 15000);
  const libraries = useData<Row[]>(scoped("/libraries", url), 60000);
  const recent = useData<Row[]>(scoped("/recent", url), 60000);
  const visible =
    servers.data?.filter(
      (s) =>
        !url.searchParams.get("server") ||
        String(s.id) === url.searchParams.get("server"),
    ) || [];
  const live = sessions.data?.filter((s) => !s.stale) || [];
  const stale = sessions.data?.filter((s) => s.stale) || [];
  const t = trends.data;
  const hoursLabel =
    num(hours) >= 24 ? `${num(hours) / 24} days` : `${hours} hours`;
  const total = sumSeries(t?.streams || [], "All streams");
  const peak =
    total[0]?.points.reduce((m, p) => Math.max(m, p[1] ?? 0), 0) || 0;
  const bands = sumSeries(t?.bandwidth || [], "Total bandwidth");
  return (
    <Page
      title="Dashboard"
      tools={
        <div className="dashboard-tools">
          <span className="live-label">
            <span className="status-dot online" />
            Auto-refresh
          </span>
          <HomeframeSelect
            aria-label="Dashboard time range"
            value={hours}
            onChange={(e) => navigate({ hours: e.target.value })}
          >
            {[
              ["1", "Last hour"],
              ["6", "Last 6 hours"],
              ["24", "Last 24 hours"],
              ["168", "Last 7 days"],
            ].map(([v, l]) => (
              <option value={v} key={v}>
                {l}
              </option>
            ))}
          </HomeframeSelect>
        </div>
      }
    >
      <div className="page-intro">
        <p>Your media, in motion.</p>
        <span>Live playback across your Plex and Tedflix servers.</span>
      </div>
      <div className="server-strip">
        {visible.map((s) => (
          <button
            key={s.id}
            className="server-status"
            onClick={() =>
              navigate({
                server:
                  String(s.id) === url.searchParams.get("server")
                    ? ""
                    : String(s.id),
              })
            }
          >
            <span className={`status-dot ${s.status}`} />
            <span>
              <strong>{s.name}</strong>
              <small>
                {s.status === "online"
                  ? `${s.stream_count} streaming`
                  : `${s.status} · ${timeAgo(s.last_success)}`}
              </small>
            </span>
            <span className="server-kind">
              {s.kind === "plex" ? "Plex" : "Tedflix"}
            </span>
          </button>
        ))}
      </div>
      {(servers.error || sessions.error || trends.error) && (
        <Message error>
          {servers.error || sessions.error || trends.error}
        </Message>
      )}
      {stale.length > 0 && (
        <Message error>
          {stale.length} sessions have stale data. Live totals exclude them
          until the server reconnects.
        </Message>
      )}
      <div className="stat-grid">
        <Stat
          label="Streaming now"
          value={sessions.data ? count(live.length) : "—"}
          detail={`${visible.filter((s) => s.status === "online").length} of ${visible.length} servers online`}
        />
        <Stat
          label="Bandwidth now"
          value={
            sessions.data
              ? `${(live.reduce((n, s) => n + num(s.bandwidth), 0) / 1000).toFixed(1)}`
              : "—"
          }
          detail="Mbps · reported stream bandwidth"
          tone="cyan"
        />
        <Stat
          label="Viewers in period"
          value={t?.available ? count(t.leaderboard.length) : "—"}
          detail={`Unique usernames · last ${hoursLabel}`}
          tone="blue"
        />
        <Stat
          label="Peak concurrency"
          value={t?.available ? Number(peak.toFixed(1)).toLocaleString() : "—"}
          detail={`Highest ${t ? `${t.step}s` : "—"} bucket average`}
          tone="peach"
        />
      </div>
      {t && !t.available && <Message error>{t.message}</Message>}
      <div className="dashboard-grid">
        <Panel
          title="The pulse"
          note={`Concurrent streams by server · ${t ? `${t.step}s` : "…"} averages`}
          className="span-8"
        >
          <LineChart
            loading={!t && !trends.error}
            series={t?.streams || []}
            label="Concurrent streams by server"
          />
        </Panel>
        <Panel
          title="Around the world"
          note={`Where people watched · last ${hoursLabel}`}
          className="span-4 map-panel"
        >
          <WorldMap
            locations={t?.locations || []}
            unlocated={t?.unlocated || 0}
          />
        </Panel>
        <Panel
          title="Bandwidth"
          note="Combined stream bandwidth · Mbps"
          className="span-8"
        >
          <LineChart
            loading={!t && !trends.error}
            series={bands}
            scale={1000}
            unit="Mbps"
            label="Stream bandwidth"
          />
        </Panel>
        <Panel
          title="Stream quality"
          note="Distinct streams seen in each time bucket"
          className="span-6"
        >
          <LineChart
            loading={!t && !trends.error}
            series={t?.quality || []}
            label="Streams by quality"
          />
        </Panel>
        <Panel
          title="Who's watching"
          note="Distinct streams seen per viewer, per time bucket"
          className="span-6"
        >
          <LineChart
            loading={!t && !trends.error}
            series={t?.viewers || []}
            label="Streams by viewer"
          />
        </Panel>
        <Panel
          title="Servers & media"
          note="Distinct streams seen by host and media type"
          className="span-8"
        >
          <LineChart
            loading={!t && !trends.error}
            series={t?.hosts || []}
            label="Streams by host and media type"
          />
        </Panel>
        <Panel
          title="The regulars"
          note={`Distinct streams seen · last ${hoursLabel}`}
          className="span-4"
        >
          <Bars
            rows={t?.leaderboard || []}
            metric="value"
            onSelect={(name) =>
              navigate(
                { username: name, page: "", search: "", user: "" },
                "/history",
              )
            }
          />
        </Panel>
      </div>
      <Section
        title="Now playing"
        tools={
          <Link to={useLink("/activity")}>
            Full activity & watch statistics →
          </Link>
        }
      >
        {!sessions.data ? (
          <Message>Checking live activity…</Message>
        ) : !sessions.data.length ? (
          <div className="empty-panel">
            A quiet moment. New streams will appear here automatically.
          </div>
        ) : (
          <div className="now-playing">
            {sessions.data.map((s) => (
              <article
                key={`${s.server_id}:${s.session_key}`}
                className={`playing-card${s.stale ? " stale" : ""}`}
              >
                <Poster
                  server={s.server_id}
                  path={
                    s.media_type === "episode"
                      ? s.grandparent_thumb || s.thumb
                      : s.thumb
                  }
                />
                <div>
                  <span className="eyebrow">{str(s.server_name)}</span>
                  <h3>{str(s.full_title || s.title)}</h3>
                  <p>
                    {str(s.friendly_name || s.user)}{" "}
                    <span>· {s.stale ? "Connection lost" : str(s.state)}</span>
                  </p>
                  <div className="playing-details">
                    <span>
                      {str(s.transcode_decision).replace(
                        "copy",
                        "Direct stream",
                      )}
                    </span>
                    <span>{(num(s.bandwidth) / 1000).toFixed(1)} Mbps</span>
                    <span>{str(s.player)}</span>
                  </div>
                  <progress
                    value={num(s.progress_percent)}
                    max={100}
                    aria-label={`${str(s.title)} playback progress`}
                  />
                  <small>
                    {duration(num(s.view_offset) / 1000)} /{" "}
                    {duration(num(s.duration) / 1000)}
                  </small>
                </div>
              </article>
            ))}
          </div>
        )}
      </Section>
      <Section
        title="Your collection"
        tools={<Link to={useLink("/libraries")}>Explore libraries →</Link>}
      >
        <div className="collection-strip">
          {[
            ["show", "TV series"],
            ["movie", "Movies / videos"],
            ["artist", "Music tracks"],
            ["clip", "Clips"],
          ].map(([type, label]) => (
            <article key={type}>
              <strong>
                {libraries.data
                  ? count(
                      libraries.data
                        .filter((l) => l.section_type === type)
                        .reduce(
                          (n, l) =>
                            n +
                            num(type === "artist" ? l.child_count : l.count),
                          0,
                        ),
                    )
                  : "—"}
              </strong>
              <span>{label}</span>
            </article>
          ))}
        </div>
        <p className="subtle">
          Current library totals across selected servers. Copies on multiple
          servers are counted separately.
        </p>
      </Section>
      <Section title="Fresh on your servers">
        <div className="poster-grid recent-grid">
          {recent.data?.slice(0, 12).map((r, i) => (
            <article key={`${r.server_id}:${r.rating_key}:${i}`}>
              <Poster
                server={r.server_id}
                path={r.thumb}
                title={str(r.title)}
              />
              <h3>{str(r.title)}</h3>
              <small>{str(r.grandparent_title)}</small>
            </article>
          ))}
        </div>
        {recent.error && <Message error>{recent.error}</Message>}
      </Section>
    </Page>
  );
}
export function Insights() {
  const url = useQuery(),
    navigate = useNavigateQuery();
  const [metric, setMetric] = useState("plays");
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const data = useData<InsightsData>(
    scoped("/insights", url, {
      days: url.searchParams.get("days") || "30",
      timezone,
    }),
    60000,
  );
  const top = useData<{ movies: Row[]; shows: Row[] }>(
    scoped("/stats", url, { days: url.searchParams.get("days") || "30" }),
    60000,
  );
  const d = data.data;
  const types = [...new Set(d?.daily.map((r) => str(r.media_type)) || [])];
  const days = [...new Set(d?.daily.map((r) => str(r.day)) || [])];
  const daily: Series[] = types.map((type) => ({
    label:
      (
        {
          episode: "TV episodes",
          movie: "Movies",
          track: "Music",
          clip: "Clips",
        } as Record<string, string>
      )[type] || type,
    points: days.map((day) => [
      Date.parse(day + (day.length === 7 ? "-01" : "") + "T12:00:00") / 1000,
      num(
        d?.daily.find((r) => r.day === day && r.media_type === type)?.[metric],
      ),
    ]),
  }));
  return (
    <Page title="Insights" tools={<DayPicker />}>
      <div className="page-intro">
        <p>The story behind the streams.</p>
        <span>
          Playback history, combined across your servers. Times in{" "}
          {timezone.replaceAll("_", " ")}.
        </span>
      </div>
      {data.error && <Message error>{data.error}</Message>}
      <div className="stat-grid">
        <Stat
          label="Recorded plays"
          value={d ? count(d.plays) : "—"}
          detail="Completed playback sessions"
        />
        <Stat
          label="Watch time"
          value={d ? duration(d.duration) : "—"}
          detail="Paused time excluded"
          tone="cyan"
        />
        <Stat
          label="Active viewers"
          value={d ? count(d.users) : "—"}
          detail="Grouped by username"
          tone="blue"
        />
        <Stat
          label="Average watch"
          value={d ? duration(d.plays ? d.duration / d.plays : 0) : "—"}
          detail="Watch time per recorded play"
          tone="peach"
        />
      </div>
      <div className="toolbar insights-toolbar">
        <span className="subtle">Explore by</span>
        <div className="segmented">
          {[
            ["plays", "Play count"],
            ["duration", "Watch time"],
          ].map(([m, label]) => (
            <button
              key={m}
              aria-pressed={metric === m}
              onClick={() => setMetric(m!)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="dashboard-grid">
        <Panel
          title={d?.grain === "month" ? "Across the months" : "Day by day"}
          note={
            metric === "plays"
              ? "Recorded plays by media type"
              : "Hours watched by media type"
          }
          className="span-12"
        >
          <LineChart
            loading={!d && !data.error}
            series={daily}
            scale={metric === "duration" ? 3600 : 1}
            unit={metric === "duration" ? "hours" : ""}
            label="Playback history trend"
          />
        </Panel>
        <Panel
          title="Prime time"
          note={`Viewing habits by day and hour · ${timezone}`}
          className="span-12"
        >
          <Heatmap rows={d?.heatmap || []} metric={metric} />
        </Panel>
        <Panel
          title="Playback methods"
          note="Direct play, direct stream and transcoding"
          className="span-4"
        >
          <Bars
            rows={
              d?.decisions.map((r) => ({
                ...r,
                label: str(r.label).replace("copy", "Direct stream"),
              })) || []
            }
            metric={metric}
          />
        </Panel>
        <Panel
          title="Favorite devices"
          note="Where people press play"
          className="span-4"
        >
          <Bars rows={d?.platforms || []} metric={metric} />
        </Panel>
        <Panel
          title="Stream resolutions"
          note="Resolution delivered to the player"
          className="span-4"
        >
          <Bars rows={d?.resolutions || []} metric={metric} />
        </Panel>
        <Panel
          title="Your audience"
          note="Combined activity by username · select to explore"
          className="span-6"
        >
          <Bars
            rows={d?.users_top || []}
            metric={metric}
            onSelect={(name) =>
              navigate(
                { username: name, page: "", search: "", user: "" },
                "/history",
              )
            }
          />
        </Panel>
        <Panel
          title="Across your servers"
          note="Playback history by host"
          className="span-6"
        >
          <Bars rows={d?.servers || []} metric={metric} />
        </Panel>
      </div>
      <div className="stats-grid favorites">
        <TopList title="Most watched movies" rows={top.data?.movies} posters />
        <TopList title="Most watched TV shows" rows={top.data?.shows} posters />
      </div>
    </Page>
  );
}
